import { useState, useEffect, lazy, Suspense } from "react";
import type { Session, SessionJump, SessionGpsPoint } from "@/services/SessionsService";
import { SessionsService } from "@/services/SessionsService";
import { DeeplinkButton } from "@/components/ui/DeeplinkButton";
import { AssignSessionDialog } from "./AssignSessionDialog";
import { CommentsTab } from "./CommentsTab";
import { SessionTimeJumpBanner } from "./SessionTimeJumpBanner";

// Lazy load the map component to avoid SSR/initialization issues
const SessionMap = lazy(() => import("./SessionMap"));

interface SessionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: number;
  userId?: number;
}

export const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  userId,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [jumps, setJumps] = useState<SessionJump[]>([]);
  const [gpsTrack, setGpsTrack] = useState<SessionGpsPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "jumps" | "map" | "comments">("overview");
  const [imageError, setImageError] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [isDownloadingFit, setIsDownloadingFit] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editDateValue, setEditDateValue] = useState("");
  const [isSavingDate, setIsSavingDate] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && sessionId) {
      loadSessionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  const loadSessionDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load session with jumps and GPS
      const sessionData = await SessionsService.getSessionDetails(sessionId, {
        includedetails: true,
        loadJumps: true,
        loadGps: true,
      });
      setSession(sessionData);
      setJumps(sessionData.jumps || []);
      setGpsTrack(sessionData.gps || []);
    } catch (err) {
      console.error("Error loading session details:", err);
      setError("Failed to load session details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadFit = async () => {
    if (!session) return;
    setIsDownloadingFit(true);
    try {
      await SessionsService.downloadFitFile(session.ID);
    } catch (err) {
      console.error("Error downloading FIT file:", err);
    } finally {
      setIsDownloadingFit(false);
    }
  };

  const handleStartEditDate = () => {
    if (!session?.startepoch) return;
    // Convert epoch to local datetime-local format (YYYY-MM-DDTHH:mm)
    const date = new Date(session.startepoch);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setEditDateValue(local.toISOString().slice(0, 16));
    setDateError(null);
    setIsEditingDate(true);
  };

  const handleCancelEditDate = () => {
    setIsEditingDate(false);
    setEditDateValue("");
    setDateError(null);
  };

  const handleSaveDate = async () => {
    if (!session || !editDateValue) return;
    setIsSavingDate(true);
    setDateError(null);
    try {
      // Convert datetime-local value to ISO 8601 with timezone offset
      const localDate = new Date(editDateValue);
      const isoString = localDate.toISOString();
      const result = await SessionsService.updateSessionDatetime(session.ID, isoString);
      setSession((prev) => prev ? { ...prev, startepoch: result.startepoch } : prev);
      setIsEditingDate(false);
      setEditDateValue("");
    } catch (err) {
      console.error("Error updating session date:", err);
      setDateError("Failed to update date");
    } finally {
      setIsSavingDate(false);
    }
  };

  console.log("SessionDetailsModal render:", { isOpen, sessionId, isLoading, error });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Session Details
              </h3>
              {session && (
                <p className="text-sm text-gray-500">
                  {SessionsService.formatDate(session.startepoch)}
                  {session.spot?.name && ` • ${session.spot.name}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {session?.garminsessionid && (
                <button
                  onClick={handleDownloadFit}
                  disabled={isDownloadingFit}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-cyan-600 border border-cyan-300 rounded-md hover:bg-cyan-50 transition-colors disabled:opacity-50"
                >
                  {isDownloadingFit ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  Download FIT
                </button>
              )}
              {session && (
                <button
                  onClick={() => setShowAssignDialog(true)}
                  className="px-3 py-1.5 text-sm font-medium text-cyan-600 border border-cyan-300 rounded-md hover:bg-cyan-50 transition-colors"
                >
                  Assign to User
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "overview"
                  ? "border-cyan-500 text-cyan-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("jumps")}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "jumps"
                  ? "border-cyan-500 text-cyan-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Jumps ({jumps.length})
            </button>
            <button
              onClick={() => setActiveTab("map")}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "map"
                  ? "border-cyan-500 text-cyan-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              GPS Track
            </button>
            <button
              onClick={() => setActiveTab("comments")}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "comments"
                  ? "border-cyan-500 text-cyan-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Comments ({session?.commentscount || 0})
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-8 w-8 text-cyan-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg className="w-12 h-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            ) : session && (
              <>
                {/* Overview Tab */}
                {activeTab === "overview" && (
                  <div className="space-y-6">
                    {/* Session Image */}
                    {session.sessionpicid && !imageError && (
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src={SessionsService.getSessionImageUrl(session.sessionpicid)}
                          alt="Session"
                          className="w-full max-h-64 object-cover"
                          onError={() => setImageError(true)}
                        />
                      </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard label="Max Height" value={session.maxheight ? `${session.maxheight.toFixed(1)}m` : "-"} isPR={session.heightpr} />
                      <StatCard label="Max Airtime" value={session.maxairtime ? `${session.maxairtime.toFixed(1)}s` : "-"} isPR={session.airtimepr} />
                      <StatCard label="Max Distance" value={session.maxdistance ? `${session.maxdistance.toFixed(1)}m` : "-"} isPR={session.distancepr} />
                      <StatCard label="Max Speed" value={session.maxspeed ? `${Math.round(session.maxspeed)} km/h` : "-"} isPR={session.speedpr} />
                    </div>

                    {/* Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard label="Total Height" value={session.totalheight ? `${session.totalheight.toFixed(1)}m` : "-"} />
                      <StatCard label="Total Airtime" value={session.totalairtime ? `${session.totalairtime.toFixed(1)}s` : "-"} />
                      <StatCard label="Total Distance" value={session.totaldistance ? `${(session.totaldistance / 1000).toFixed(2)} km` : "-"} />
                      <StatCard label="Time on Water" value={SessionsService.formatDuration(session.timeonwater)} />
                    </div>

                    {/* Session Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoCard title="Session Info">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Session ID</span>
                          {userId ? (
                            <DeeplinkButton
                              userId={userId}
                              tab="sessions"
                              sessionId={session.ID}
                              variant="badge"
                              title="Open session deeplink in new tab"
                            />
                          ) : (
                            <span className="text-gray-900 font-medium">#{session.ID}</span>
                          )}
                        </div>
                        <div className="flex justify-between text-sm items-center">
                          <span className="text-gray-500">Date</span>
                          {isEditingDate ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                type="datetime-local"
                                value={editDateValue}
                                onChange={(e) => setEditDateValue(e.target.value)}
                                className="text-sm border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                disabled={isSavingDate}
                              />
                              <button
                                onClick={handleSaveDate}
                                disabled={isSavingDate}
                                className="text-xs px-2 py-0.5 bg-cyan-500 text-white rounded hover:bg-cyan-600 disabled:opacity-50"
                              >
                                {isSavingDate ? "..." : "Save"}
                              </button>
                              <button
                                onClick={handleCancelEditDate}
                                disabled={isSavingDate}
                                className="text-xs px-2 py-0.5 text-gray-500 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              {dateError && <span className="text-xs text-red-500">{dateError}</span>}
                            </div>
                          ) : (
                            <span className="text-gray-900 font-medium flex items-center gap-1">
                              {SessionsService.formatDate(session.startepoch)}
                              <button
                                onClick={handleStartEditDate}
                                className="p-0.5 text-gray-400 hover:text-cyan-600 transition-colors"
                                title="Edit date"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            </span>
                          )}
                        </div>
                        <InfoRow label="Spot" value={session.spot?.name || "-"} />
                        <InfoRow label="Wind Direction" value={SessionsService.getWindDirection(session.winddir)} />
                        <InfoRow label="Visibility" value={session.visiblesession ? "Visible" : "Hidden"} />
                        <InfoRow label="Jump Count" value={String(jumps.length)} />
                        {session.garminsessionid && (
                          <InfoRow label="Garmin Session ID" value={session.garminsessionid} />
                        )}
                      </InfoCard>

                      <InfoCard title="Equipment & Device">
                        <InfoRow label="Kite" value={session.kite || "-"} />
                        <InfoRow label="Board Type" value={SessionsService.getBoardTypeLabel(session.boardtype)} />
                        <InfoRow label="Device" value={session.device?.brand || "-"} />
                        <InfoRow label="Model" value={session.device?.model || "-"} />
                      </InfoCard>
                    </div>

                    {/* Remark */}
                    {session.remark && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600">{session.remark}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Jumps Tab */}
                {activeTab === "jumps" && (
                  <div className="space-y-2">
                    {jumps.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        No jumps recorded for this session
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Height</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Airtime</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Speed</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {jumps.map((jump, index) => (
                              <tr key={jump.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {jump.nr || index + 1}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {jump.timestring || "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                                  {jump.height ? `${jump.height.toFixed(1)}m` : "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                  {jump.airtime ? `${jump.airtime.toFixed(1)}s` : "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                  {jump.distance ? `${jump.distance.toFixed(1)}m` : "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                  {jump.maxspeedreached ? `${Math.round(jump.maxspeedreached * 3.6)} km/h` : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Map Tab */}
                {activeTab === "map" && (
                  <>
                  <SessionTimeJumpBanner session={session} onRepaired={loadSessionDetails} />
                  <div className="h-[500px] rounded-lg overflow-hidden border border-gray-200">
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <svg className="animate-spin h-8 w-8 text-cyan-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                    }>
                      <SessionMap
                        gpsTrack={gpsTrack}
                        jumps={jumps}
                        session={session}
                      />
                    </Suspense>
                  </div>
                  </>
                )}

                {/* Comments Tab */}
                {activeTab === "comments" && (
                  <CommentsTab
                    sessionId={session.ID}
                    onCommentsCountChange={(count) => {
                      setSession((prev) =>
                        prev ? { ...prev, commentscount: count } : prev
                      );
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Assign Session Dialog */}
          {session && (
            <AssignSessionDialog
              open={showAssignDialog}
              onClose={() => setShowAssignDialog(false)}
              sessionId={session.ID}
              currentRemark={session.remark}
              onSuccess={loadSessionDetails}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{ label: string; value: string; isPR?: boolean }> = ({ label, value, isPR }) => (
  <div className={`rounded-lg p-4 ${isPR ? "bg-blue-50 border border-blue-200" : "bg-gray-50"}`}>
    {isPR && (
      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded mb-2">
        Personal Record
      </span>
    )}
    <p className={`text-2xl font-bold ${isPR ? "text-cyan-600" : "text-gray-900"}`}>{value}</p>
    <p className="text-sm text-gray-500 mt-1">{label}</p>
  </div>
);

// Info Card Component
const InfoCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);

// Info Row Component
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 font-medium">{value}</span>
  </div>
);
